import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Switch } from '@/components/ui/switch';
import {
  Download, Loader2, RefreshCw, Calendar,
  TrendingUp, TrendingDown, AlertCircle, BarChart3,
  DollarSign, FileText, Percent, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { MultiSelect, MultiSelectOption } from '@/app/components/common/MultiSelect';
import {
  fetchSalesReportData,
  SalesReportParams,
  SalesReportResponse,
  SalesReportRow
} from '@/app/services/supabaseEdgeFunctionService';
import { listSites } from '@/app/services/siteService';
import { listParties, listPartyGroups } from '@/app/services/partyService';

const SalesReportPage: React.FC = () => {
  // State management - default to today
  const [startDate, setStartDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([]);
  const [selectedPartyIds, setSelectedPartyIds] = useState<number[]>([]);
  const [selectedPartyGroupIds, setSelectedPartyGroupIds] = useState<number[]>([]);
  const [selectedResponsiblePartyIds, setSelectedResponsiblePartyIds] = useState<number[]>([]);
  const [aggregationLevel, setAggregationLevel] = useState<'detail' | 'party' | 'site' | 'party_group' | 'overall'>('detail');
  const [summaryOnly, setSummaryOnly] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Prepare params for API calls
  const apiParams: SalesReportParams = {
    start_date: startDate,
    end_date: endDate,
    ...(selectedSiteIds.length > 0 && { site_ids: selectedSiteIds }),
    ...(selectedPartyIds.length > 0 && { party_ids: selectedPartyIds }),
    ...(selectedPartyGroupIds.length > 0 && { party_group_ids: selectedPartyGroupIds }),
    ...(selectedResponsiblePartyIds.length > 0 && { responsible_party_ids: selectedResponsiblePartyIds }),
    aggregation_level: aggregationLevel,
    summary_only: summaryOnly
  };

  // Fetch sites for dropdown
  const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites-for-sales-report'],
    queryFn: () => listSites({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch parties for dropdown
  const { data: partiesData = { items: [] }, isLoading: isLoadingParties } = useQuery({
    queryKey: ['parties-for-sales-report'],
    queryFn: () => listParties({ limit: 500, filters: JSON.stringify([{ field: "is_active", op: "eq", value: true }]) }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch party groups for dropdown
  const { data: partyGroupsData = { items: [] }, isLoading: isLoadingPartyGroups } = useQuery({
    queryKey: ['party-groups-for-sales-report'],
    queryFn: () => listPartyGroups({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch EMPLOYEE party group to get its ID
  const { data: employeeGroupData } = useQuery({
    queryKey: ['employee-party-group'],
    queryFn: () => listPartyGroups({
      filters: JSON.stringify([{ field: "name", op: "eq", value: "EMPLOYEE" }]),
      limit: 1
    }),
    staleTime: 15 * 60 * 1000,
  });

  const employeeGroupId = employeeGroupData?.items?.[0]?.id;

  // Fetch EMPLOYEE parties for responsible party dropdown
  const { data: responsiblePartiesData = { items: [] }, isLoading: isLoadingResponsibleParties } = useQuery({
    queryKey: ['responsible-parties-for-sales-report', employeeGroupId],
    queryFn: () => listParties({
      limit: 500,
      filters: JSON.stringify([
        { field: "party_group_id", op: "eq", value: employeeGroupId },
        { field: "is_active", op: "eq", value: true }
      ])
    }),
    enabled: !!employeeGroupId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch sales report data
  const { data: reportData, isLoading: isLoadingReport, refetch: refetchReport, error: reportError } = useQuery({
    queryKey: ['sales-report', apiParams],
    queryFn: () => fetchSalesReportData(apiParams),
    retry: 1,
    onError: (error: any) => {
      toast.error(error.message || 'Failed to fetch sales report');
    }
  });

  // Transform data for multi-select components
  const siteOptions: MultiSelectOption[] = sitesData.items?.map(site => ({
    id: site.id,
    name: site.name,
    extra: site.code
  })) || [];

  const partyOptions: MultiSelectOption[] = partiesData.items?.map(party => ({
    id: party.id,
    name: party.name,
    extra: party.phone || party.party_code
  })) || [];

  const partyGroupOptions: MultiSelectOption[] = partyGroupsData.items?.map(group => ({
    id: group.id,
    name: group.name
  })) || [];

  const responsiblePartyOptions: MultiSelectOption[] = responsiblePartiesData.items?.map(party => ({
    id: party.id,
    name: party.name,
    extra: party.phone || party.party_code
  })) || [];

  // Date preset handler
  const handleDatePreset = (preset: string) => {
    const today = new Date();

    switch (preset) {
      case 'today':
        setStartDate(format(today, 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        setStartDate(format(yesterday, 'yyyy-MM-dd'));
        setEndDate(format(yesterday, 'yyyy-MM-dd'));
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        setStartDate(format(lastMonth, 'yyyy-MM-dd'));
        setEndDate(format(lastMonthEnd, 'yyyy-MM-dd'));
        break;
      case 'thisMonth':
        setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
      case 'ytd':
        setStartDate(format(startOfYear(today), 'yyyy-MM-dd'));
        setEndDate(format(today, 'yyyy-MM-dd'));
        break;
    }
  };

  // Manual refresh function
  const handleRefresh = async () => {
    await refetchReport();
    toast.success('Report refreshed successfully');
  };

  // Export function
  const handleExport = () => {
    if (!reportData?.data && !reportData?.summary) {
      toast.error('No data to export');
      return;
    }

    // Create comprehensive CSV data
    const csvRows = [];

    // Headers
    csvRows.push(['Sales Report']);
    csvRows.push([`Period: ${startDate} to ${endDate}`]);
    csvRows.push([`Aggregation Level: ${aggregationLevel}`]);

    if (selectedSiteIds.length > 0) {
      const selectedSites = sitesData.items?.filter(s => selectedSiteIds.includes(s.id)).map(s => s.name).join(', ');
      csvRows.push([`Sites: ${selectedSites}`]);
    }
    if (selectedPartyIds.length > 0) {
      csvRows.push([`Selected ${selectedPartyIds.length} parties`]);
    }
    if (selectedPartyGroupIds.length > 0) {
      const selectedGroups = partyGroupsData.items?.filter(g => selectedPartyGroupIds.includes(g.id)).map(g => g.name).join(', ');
      csvRows.push([`Party Groups: ${selectedGroups}`]);
    }
    if (selectedResponsiblePartyIds.length > 0) {
      const selectedResponsibleParties = responsiblePartiesData.items?.filter(p => selectedResponsiblePartyIds.includes(p.id)).map(p => p.name).join(', ');
      csvRows.push([`Responsible Parties: ${selectedResponsibleParties}`]);
    }

    csvRows.push(['']);

    // Data rows
    if (reportData.data && reportData.data.length > 0) {
      // Headers based on aggregation level
      const headers = [];
      if (aggregationLevel !== 'overall') {
        if (aggregationLevel === 'party' || aggregationLevel === 'detail') headers.push('Party Name');
        if (aggregationLevel === 'party_group') headers.push('Party Group');
        if (aggregationLevel === 'site') headers.push('Site');
      }
      headers.push(
        'Invoices', 'Cancelled', 'Qty Sold', 'Unique Items',
        'Gross Sales', 'Discount', 'Net Sales', 'Tax',
        'Total Invoice Value', 'Avg Order Value', 'Avg Discount %'
      );
      csvRows.push(headers);

      // Data rows
      reportData.data.forEach(row => {
        const dataRow = [];
        if (aggregationLevel !== 'overall') {
          if (aggregationLevel === 'party' || aggregationLevel === 'detail') {
            dataRow.push(row.party_name || 'N/A');
          }
          if (aggregationLevel === 'party_group') {
            dataRow.push(row.party_group_name || 'N/A');
          }
          if (aggregationLevel === 'site') {
            dataRow.push(row.site_name || 'N/A');
          }
        }
        dataRow.push(
          row.invoice_count,
          row.cancelled_invoice_count,
          row.total_quantity_sold,
          row.unique_items_count,
          row.gross_sales.toFixed(2),
          row.discount_amount.toFixed(2),
          row.net_sales.toFixed(2),
          row.tax_amount.toFixed(2),
          row.total_invoice_value.toFixed(2),
          row.avg_order_value.toFixed(2),
          row.avg_discount_percentage.toFixed(2)
        );
        csvRows.push(dataRow);
      });
    }

    // Convert to CSV string
    const csvContent = csvRows.map(row => row.join(',')).join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-report-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Currency formatter
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Calculate summary metrics
  const summaryMetrics = reportData?.metadata?.summary || {
    total_gross_sales: 0,
    total_net_sales: 0,
    total_invoice_value: 0,
    total_invoices: 0,
    average_invoice_value: 0
  };

  const totalTax = reportData?.data?.reduce((sum, row) => sum + (row.tax_amount || 0), 0) || 0;
  const totalDiscount = reportData?.data?.reduce((sum, row) => sum + (row.discount_amount || 0), 0) || 0;
  const avgDiscountRate = summaryMetrics.total_gross_sales > 0
    ? (totalDiscount / summaryMetrics.total_gross_sales) * 100
    : 0;

  // Chart colors
  const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];

  // Prepare data for charts
  const chartData = reportData?.data || [];

  // Top performers (for pie chart)
  const topPerformers = chartData
    .sort((a, b) => b.total_invoice_value - a.total_invoice_value)
    .slice(0, 6)
    .map(item => ({
      name: item.party_name || item.party_group_name || item.site_name || 'Unknown',
      value: item.total_invoice_value
    }));

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Sales Performance Report
          </CardTitle>
          <CardDescription>
            Comprehensive analysis of sales data with detailed metrics and insights
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Quick Date Presets */}
          <div className="flex gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('today')}>
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('yesterday')}>
              Yesterday
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('lastMonth')}>
              Last Month
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('thisMonth')}>
              This Month
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDatePreset('ytd')}>
              Year to Date
            </Button>
          </div>

          {/* Filters Grid */}
          <div className="space-y-4">
            {/* First Row: Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Aggregation Level</Label>
                <Select value={aggregationLevel} onValueChange={(v: any) => setAggregationLevel(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="detail">Detailed</SelectItem>
                    <SelectItem value="party">By Party</SelectItem>
                    <SelectItem value="site">By Site</SelectItem>
                    <SelectItem value="party_group">By Party Group</SelectItem>
                    <SelectItem value="overall">Overall Summary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Summary Only
                </Label>
                <div className="flex items-center h-10">
                  <Switch
                    checked={summaryOnly}
                    onCheckedChange={setSummaryOnly}
                  />
                  <span className="ml-2 text-sm text-muted-foreground">
                    {summaryOnly ? 'Summary view' : 'Detailed view'}
                  </span>
                </div>
              </div>
            </div>

            {/* Second Row: Multi-Select Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Sites</Label>
                <MultiSelect
                  options={siteOptions}
                  value={selectedSiteIds}
                  onChange={setSelectedSiteIds}
                  placeholder="All sites"
                  searchPlaceholder="Search sites..."
                  loading={isLoadingSites}
                />
              </div>
              <div className="space-y-2">
                <Label>Parties</Label>
                <MultiSelect
                  options={partyOptions}
                  value={selectedPartyIds}
                  onChange={setSelectedPartyIds}
                  placeholder="All parties"
                  searchPlaceholder="Search parties..."
                  loading={isLoadingParties}
                />
              </div>
              <div className="space-y-2">
                <Label>Party Groups</Label>
                <MultiSelect
                  options={partyGroupOptions}
                  value={selectedPartyGroupIds}
                  onChange={setSelectedPartyGroupIds}
                  placeholder="All party groups"
                  searchPlaceholder="Search groups..."
                  loading={isLoadingPartyGroups}
                />
              </div>
              <div className="space-y-2">
                <Label>Responsible Parties</Label>
                <MultiSelect
                  options={responsiblePartyOptions}
                  value={selectedResponsiblePartyIds}
                  onChange={setSelectedResponsiblePartyIds}
                  placeholder="All employees"
                  searchPlaceholder="Search employees..."
                  loading={isLoadingResponsibleParties}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button onClick={handleRefresh} disabled={isLoadingReport}>
                {isLoadingReport ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                disabled={isLoadingReport || (!reportData?.data?.length && !reportData?.summary?.length)}
              >
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <Badge variant="secondary" className="text-xs">Count</Badge>
              </div>
              <p className="text-xl font-semibold tracking-tight">{summaryMetrics.total_invoices}</p>
              <p className="text-xs text-muted-foreground">Total Invoices</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <Badge variant="secondary" className="text-xs">Gross</Badge>
              </div>
              <p className="text-xl font-semibold tracking-tight">{formatCurrency(summaryMetrics.total_gross_sales)}</p>
              <p className="text-xs text-muted-foreground">Gross Sales</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Percent className="h-4 w-4 text-muted-foreground" />
                <Badge variant="destructive" className="text-xs">Discount</Badge>
              </div>
              <p className="text-xl font-semibold tracking-tight">{formatCurrency(totalDiscount)}</p>
              <p className="text-xs text-muted-foreground">{avgDiscountRate.toFixed(1)}% of sales</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <Badge variant="secondary" className="text-xs">Net</Badge>
              </div>
              <p className="text-xl font-semibold tracking-tight">{formatCurrency(summaryMetrics.total_net_sales)}</p>
              <p className="text-xs text-muted-foreground">Net Sales</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <Badge variant="secondary" className="text-xs">Tax</Badge>
              </div>
              <p className="text-xl font-semibold tracking-tight">{formatCurrency(totalTax)}</p>
              <p className="text-xs text-muted-foreground">Total Tax</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                <Badge variant="default" className="text-xs">AOV</Badge>
              </div>
              <p className="text-xl font-semibold tracking-tight">{formatCurrency(summaryMetrics.average_invoice_value)}</p>
              <p className="text-xs text-muted-foreground">Avg Order Value</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Data */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="detailed">Detailed Analysis</TabsTrigger>
          <TabsTrigger value="data">Data Table</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Sales Distribution Chart */}
          {chartData.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Sales Distribution</CardTitle>
                  <CardDescription>Revenue breakdown by top performers</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={{}} className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={topPerformers}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {topPerformers.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Sales Metrics Comparison</CardTitle>
                  <CardDescription>Gross vs Net vs Final Invoice Value</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={{}} className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          {
                            name: 'Sales Metrics',
                            'Gross Sales': summaryMetrics.total_gross_sales,
                            'Discount': totalDiscount,
                            'Net Sales': summaryMetrics.total_net_sales,
                            'Tax': totalTax,
                            'Invoice Value': summaryMetrics.total_invoice_value
                          }
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="Gross Sales" fill="#10b981" />
                        <Bar dataKey="Discount" fill="#ef4444" />
                        <Bar dataKey="Net Sales" fill="#3b82f6" />
                        <Bar dataKey="Tax" fill="#f59e0b" />
                        <Bar dataKey="Invoice Value" fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-muted-foreground">
                  {isLoadingReport ? (
                    <>
                      <Loader2 className="h-8 w-8 animate-spin mb-4" />
                      <p>Loading sales report data...</p>
                    </>
                  ) : reportError ? (
                    <>
                      <AlertCircle className="h-8 w-8 mb-4 text-destructive" />
                      <p>Error loading data: {(reportError as any).message}</p>
                    </>
                  ) : (
                    <>
                      <BarChart3 className="h-8 w-8 mb-4" />
                      <p>No data available for the selected filters</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="detailed" className="space-y-4">
          {chartData.length > 0 && aggregationLevel !== 'overall' ? (
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics by {aggregationLevel === 'party' ? 'Party' : aggregationLevel === 'site' ? 'Site' : aggregationLevel === 'party_group' ? 'Party Group' : 'Item'}</CardTitle>
                <CardDescription>Detailed breakdown of sales performance</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={{}} className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData.slice(0, 10)}
                      margin={{ top: 20, right: 30, left: 50, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey={aggregationLevel === 'party' ? 'party_name' : aggregationLevel === 'site' ? 'site_name' : aggregationLevel === 'party_group' ? 'party_group_name' : 'party_name'}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis />
                      <Tooltip formatter={(value: number) => typeof value === 'number' ? formatCurrency(value) : value} />
                      <Legend />
                      <Bar dataKey="gross_sales" name="Gross Sales" fill="#10b981" />
                      <Bar dataKey="net_sales" name="Net Sales" fill="#3b82f6" />
                      <Bar dataKey="total_invoice_value" name="Invoice Value" fill="#8b5cf6" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>
          ) : aggregationLevel === 'overall' ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mb-4" />
                  <p>Detailed analysis not available for overall aggregation</p>
                  <p className="text-sm mt-2">Please select a different aggregation level to see detailed charts</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mb-4" />
                  <p>No data available for detailed analysis</p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sales Data Table</CardTitle>
              <CardDescription>
                Detailed sales data for the selected period and filters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {aggregationLevel !== 'overall' && (
                        <>
                          {(aggregationLevel === 'party' || aggregationLevel === 'detail') && <TableHead>Party</TableHead>}
                          {aggregationLevel === 'party_group' && <TableHead>Party Group</TableHead>}
                          {aggregationLevel === 'site' && <TableHead>Site</TableHead>}
                        </>
                      )}
                      <TableHead className="text-center">Invoices</TableHead>
                      <TableHead className="text-center">Cancelled</TableHead>
                      <TableHead className="text-center">Qty Sold</TableHead>
                      <TableHead className="text-right">Gross Sales</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Net Sales</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total Value</TableHead>
                      <TableHead className="text-right">AOV</TableHead>
                      <TableHead className="text-center">Disc %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingReport ? (
                      <TableRow>
                        <TableCell colSpan={aggregationLevel === 'overall' ? 10 : 11} className="text-center py-8">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                          <p className="mt-2 text-muted-foreground">Loading sales data...</p>
                        </TableCell>
                      </TableRow>
                    ) : chartData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={aggregationLevel === 'overall' ? 10 : 11} className="text-center py-8 text-muted-foreground">
                          No data available for the selected filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      chartData.map((row: SalesReportRow, index) => (
                        <TableRow key={index}>
                          {aggregationLevel !== 'overall' && (
                            <>
                              {(aggregationLevel === 'party' || aggregationLevel === 'detail') && (
                                <TableCell className="font-medium">
                                  {row.party_name || 'N/A'}
                                </TableCell>
                              )}
                              {aggregationLevel === 'party_group' && (
                                <TableCell className="font-medium">
                                  {row.party_group_name || 'N/A'}
                                </TableCell>
                              )}
                              {aggregationLevel === 'site' && (
                                <TableCell className="font-medium">
                                  {row.site_name || 'N/A'}
                                </TableCell>
                              )}
                            </>
                          )}
                          <TableCell className="text-center">
                            <Badge variant="secondary">{row.invoice_count}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {row.cancelled_invoice_count > 0 ? (
                              <Badge variant="destructive">{row.cancelled_invoice_count}</Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">{row.total_quantity_sold}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(row.gross_sales)}
                          </TableCell>
                          <TableCell className="text-right text-destructive">
                            {formatCurrency(row.discount_amount)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(row.net_sales)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(row.tax_amount)}
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(row.total_invoice_value)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(row.avg_order_value)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={row.avg_discount_percentage > 10 ? "destructive" : "secondary"}
                              className="text-xs"
                            >
                              {row.avg_discount_percentage.toFixed(1)}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Data Summary Footer */}
              {chartData.length > 0 && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      Showing {chartData.length} {aggregationLevel === 'overall' ? 'summary' : 'records'}
                    </div>
                    <div className="flex gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Total Gross:</span>{' '}
                        <span className="font-medium">{formatCurrency(summaryMetrics.total_gross_sales)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Net:</span>{' '}
                        <span className="font-medium">{formatCurrency(summaryMetrics.total_net_sales)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Invoice Value:</span>{' '}
                        <span className="font-bold">{formatCurrency(summaryMetrics.total_invoice_value)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalesReportPage;